import { DomPurifyConfig } from '@/config/DomPurify.config.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { QuestionSegment4Props } from '@/views/process/components/quizInialized/questions/QuestionSegment4.tsx';
import DOMPurify from 'dompurify';
import parseHtml from 'html-react-parser';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';

type SingleQuestionProps = QuestionSegment4Props & {
  isCurrent?: boolean;
  onlyTwoOptions?: boolean;
  readonly?: boolean;
  withoutSubmitButton?: boolean;
};

const SingleQuestionForm: React.FC<SingleQuestionProps> = ({
  disabled,
  isCurrent,
  onAnswerSelected,
  onlyTwoOptions,
  questionDefinition,
  readonly,
  withoutSubmitButton,
}) => {
  const { t } = useTranslationWithPrefix('views.process.quizQuestions');
  const selfRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isCurrent) {
      return;
    }
    let timeout: ReturnType<typeof setTimeout>;
    if (selfRef.current) {
      timeout = setTimeout(() => {
        selfRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    }
    return () => clearTimeout(timeout);
  }, [isCurrent, questionDefinition.question]);

  const { answerDefinition, question } = questionDefinition;
  const [formData, setFormData] = useState<number | null>(null);

  const handleCheck = (answerIndex: number) => () => {
    setFormData(answerIndex);
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormData(null);
    onAnswerSelected({
      answerIndex: Number(formData),
      discriminatorType: questionDefinition.discriminatorType,
    });
  };

  const handleSingleOptionClick = (answerIndex: number) => () => {
    setFormData(null);
    onAnswerSelected({
      answerIndex: Number(answerIndex),
      discriminatorType: questionDefinition.discriminatorType,
    });
  };
  return (
    <Form className="flex flex-column gap-l" onSubmit={handleSubmit}>
      {onlyTwoOptions ? (
        <p className="lead">{parseHtml(DOMPurify.sanitize(question, DomPurifyConfig))}</p>
      ) : (
        <p className="lead fw-bold">{parseHtml(DOMPurify.sanitize(question, DomPurifyConfig))}</p>
      )}
      <Form.Group>
        {onlyTwoOptions ? (
          <div className="flex flex-col flex-md-row max-w-(--breakpoint-md) gap-l">
            {answerDefinition?.options.map(option => (
              <Button
                className="flex-1"
                disabled={readonly || disabled}
                key={option.index}
                onClick={handleSingleOptionClick(option.index)}
                variant={formData === option.index ? 'secondary' : 'outline-secondary'}
              >
                {option.answer}
              </Button>
            ))}
          </div>
        ) : (
          <div className="flex flex-column max-w-(--breakpoint-md) gap-l">
            {answerDefinition?.options.map(option => (
              <Form.Check
                checked={formData === option.index}
                className="mb-2 lead"
                id={option.index?.toString()}
                key={option.index}
                label={option.answer}
                onChange={handleCheck(option.index)}
                type="radio"
              />
            ))}
          </div>
        )}
      </Form.Group>
      <div ref={selfRef} />
      {!withoutSubmitButton && (
        <Button
          className="px-sm-5 align-self-sm-start"
          disabled={!Number.isInteger(formData) || disabled}
          type="submit"
        >
          {t('buttons.next')}
        </Button>
      )}
    </Form>
  );
};

export default SingleQuestionForm;
